import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Drawer,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../../context/AuthContext';
import RecipeView from '../../components/recipes/RecipeView';
import { getRecipeEmoji } from '../../utils/getRecipeEmoji';
import api from '../../services/api';
import { grey, primary, badges } from '../../styles/tokens';

const DIETARY_TRANSLATIONS = {
  vegetarian: 'Vegetarien',
  vegan: 'Vegan',
  'gluten-free': 'Sans gluten',
  'dairy-free': 'Sans lactose',
  paleo: 'Paleo',
  keto: 'Keto',
  other: 'Autre',
};

const mealTranslations = {
  breakfast: 'Petit-dej',
  lunch: 'Dejeuner',
  snack: 'Encas',
  dinner: 'Diner',
};

const RecipeList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [filterMealType, setFilterMealType] = useState('all');
  const [filterDietary, setFilterDietary] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isCoach = user?.role === 'coach';
  const basePath = isAdminRoute ? '/admin' : '/coach';

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isAdminRoute) {
        // Admin: fetch from admin global recipes API
        const data = await api.get('/admin/recipes?limit=500');
        // API returns { recipes, pagination } - extract recipes array
        const recipesArray = data.recipes || [];
        // Admin recipes are all global, can edit/delete all
        setRecipes(recipesArray.map(r => ({ ...r, source: 'global', canEdit: true })));
      } else {
        // Coach: fetch from multi-tenant API (coach recipes + global)
        const allRecipes = await api.get('/coach/recipes/all?limit=500');
        // allRecipes includes source: 'coach' or 'global'
        // Coaches can only edit their own recipes (source: 'coach')
        setRecipes(allRecipes.map(r => ({
          ...r,
          canEdit: r.source === 'coach'
        })));
      }
    } catch (err) {
      console.error('Error fetching recipes:', err);
      setError(err.message || 'Impossible de charger les recettes.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    setDrawerOpen(true);
  };

  const handleEditRecipe = (recipe) => {
    if (!recipe.canEdit && isCoach) {
      setError('Vous ne pouvez pas modifier les recettes de la bibliotheque. Creez votre propre version.');
      return;
    }
    navigate(`${basePath}/recipes/edit/${recipe._id}`);
  };

  const handleDeleteRecipeClick = (recipe) => {
    if (!recipe.canEdit && isCoach) {
      setError('Vous ne pouvez pas supprimer les recettes de la bibliotheque.');
      return;
    }
    setRecipeToDelete(recipe);
    setDeleteDialogOpen(true);
  };

  const handleDeleteRecipeConfirm = async () => {
    try {
      if (isAdminRoute) {
        // Admin: delete from global recipes
        await api.delete(`/admin/recipes/${recipeToDelete._id}`);
      } else {
        // Coach: delete from tenant DB
        await api.delete(`/coach/recipes/${recipeToDelete._id}`);
      }

      setRecipes(prevRecipes => prevRecipes.filter(recipe => recipe._id !== recipeToDelete._id));
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
    } catch (err) {
      console.error('Erreur lors de la suppression de la recette:', err);
      setError(err.message || 'Erreur lors de la suppression.');
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setRecipeToDelete(null);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRecipe(null);
  };

  const filteredRecipes = recipes.filter(recipe => {
    const matchesMealType = filterMealType === 'all' || recipe.mealType === filterMealType;
    const matchesDietary = filterDietary === 'all' || (recipe.category && recipe.category.includes(filterDietary));
    const matchesSearch = recipe.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesMealType && matchesDietary && matchesSearch;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Recettes - {isAdminRoute ? 'Admin' : 'Coach'} Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h1">Recettes</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate(`${basePath}/recipes/create`)}
          >
            Ajouter
          </Button>
        </Stack>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Info for coaches */}
        {isCoach && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Les recettes "Bibliotheque" sont partagees et ne peuvent pas etre modifiees. "Ma recette" indique vos propres creations.
          </Alert>
        )}

        {/* Filters */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Repas</InputLabel>
            <Select
              value={filterMealType}
              label="Repas"
              onChange={(e) => setFilterMealType(e.target.value)}
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="breakfast">Petit-dej</MenuItem>
              <MenuItem value="lunch">Dejeuner</MenuItem>
              <MenuItem value="snack">Encas</MenuItem>
              <MenuItem value="dinner">Diner</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Regime</InputLabel>
            <Select
              value={filterDietary}
              label="Regime"
              onChange={(e) => setFilterDietary(e.target.value)}
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="vegetarian">Vegetarien</MenuItem>
              <MenuItem value="vegan">Vegan</MenuItem>
              <MenuItem value="gluten-free">Sans gluten</MenuItem>
              <MenuItem value="paleo">Paleo</MenuItem>
              <MenuItem value="keto">Keto</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, maxWidth: { sm: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: grey[400] }} />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        {/* Recipe Grid */}
        <Grid container spacing={2}>
          {filteredRecipes.map((recipe) => (
            <Grid item xs={12} sm={6} md={4} key={recipe._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Emoji Header */}
                <Box
                  onClick={() => handleViewRecipe(recipe)}
                  sx={{
                    height: 100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: grey[100],
                    fontSize: '2.5rem',
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                >
                  {getRecipeEmoji(recipe.name)}

                  {/* Source badge for coaches */}
                  {isCoach && (
                    <Chip
                      label={recipe.source === 'global' ? 'Bibliotheque' : 'Ma recette'}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: recipe.source === 'global' ? badges.library.bg : badges.personal.bg,
                        color: recipe.source === 'global' ? badges.library.text : badges.personal.text,
                        border: `1px solid ${recipe.source === 'global' ? badges.library.border : badges.personal.border}`,
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        height: 24,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  )}
                </Box>

                <CardContent sx={{ flex: 1 }}>
                  {/* Title */}
                  <Typography
                    onClick={() => handleViewRecipe(recipe)}
                    variant="h4"
                    sx={{
                      mb: 1.5,
                      cursor: 'pointer',
                      '&:hover': { color: primary[500] },
                    }}
                  >
                    {recipe.name}
                  </Typography>

                  {/* Tags */}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 2, gap: 0.5 }}>
                    <Chip
                      label={mealTranslations[recipe.mealType] || recipe.mealType}
                      size="small"
                    />
                    {recipe.category && recipe.category.slice(0, 1).map((cat, idx) => (
                      <Chip
                        key={idx}
                        label={DIETARY_TRANSLATIONS[cat] || cat}
                        size="small"
                      />
                    ))}
                  </Stack>

                  {/* Actions */}
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleViewRecipe(recipe)}
                    >
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    </IconButton>

                    {/* Edit button - only for own recipes or admin */}
                    {recipe.canEdit && (
                      <IconButton
                        size="small"
                        onClick={() => handleEditRecipe(recipe)}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}

                    {/* Delete button - only for own recipes or admin */}
                    {recipe.canEdit && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRecipeClick(recipe)}
                        sx={{ '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredRecipes.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2">
              Aucune recette trouvee.
            </Typography>
          </Box>
        )}

        {/* Recipe View Drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleCloseDrawer}
          PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
        >
          {selectedRecipe && <RecipeView recipe={selectedRecipe} onClose={handleCloseDrawer} />}
        </Drawer>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
          <DialogTitle>Confirmer la suppression</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Etes-vous sur de vouloir supprimer <strong>{recipeToDelete?.name}</strong> ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteDialogClose} variant="outlined">
              Annuler
            </Button>
            <Button onClick={handleDeleteRecipeConfirm} color="error" variant="contained">
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default RecipeList;
